import { useEffect, useMemo, useRef, useState } from "react";
import { useBookingDraft } from "./useBookingDraft.js";
import { BuildingSelector } from "./BuildingSelector.js";
import { ServiceSelector } from "./ServiceSelector.js";
import { AddressForm, validateAddressForm } from "./AddressForm.js";
import { DateTimeStep } from "./DateTimeStep.js";
import { BookingReview } from "./BookingReview.js";
import { PriceSummaryPanel, MobileStickyPrice, usePriceBreakdown } from "./PriceSummaryPanel.js";
import { apiPost } from "../../client/apiClient.js";
import type { ApiResult } from "../../shared/types.js";
import { SuccessState, ErrorState } from "../../components/States.js";
import { CTAButton } from "../../components/Buttons.js";
import { CheckIcon } from "../../components/Icons.js";

const STEPS = ["Gebouw", "Keuringen", "Adres", "Datum & tijd", "Bevestiging"];

export function BookingWizard() {
  const { state, update, reset } = useBookingDraft();
  const { breakdown, loading } = usePriceBreakdown(state.building, state.service, state.contact.postalCode || undefined);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmedBooking, setConfirmedBooking] = useState<any>(null);
  const renderedAt = useRef(Date.now());

  const canProceed = useMemo(() => {
    // Wettelijk geen asbestattest vereist (bv. bouwjaar 2001 of later) → nergens
    // doorboeken, ongeacht op welke stap iemand staat.
    if (breakdown?.attestNotRequired) return false;
    if (state.step === 3) return Object.keys(validateAddressForm(state.contact)).length === 0;
    if (state.step === 4) return Boolean(state.slot?.date && state.slot?.time);
    return true;
  }, [state, breakdown]);

  function goNext() {
    if (state.step < 5) update({ step: state.step + 1 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function goBack() {
    if (state.step > 1) update({ step: state.step - 1 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function confirmBooking() {
    setSubmitError(null);
    if (breakdown?.attestNotRequired) {
      setSubmitError("Voor dit bouwjaar is een asbestattest wettelijk niet verplicht — deze boeking kan niet bevestigd worden.");
      return;
    }
    if (!state.acceptedTerms || !state.acceptedPrivacy) {
      setSubmitError("Ga akkoord met de algemene voorwaarden en de privacyverklaring om verder te gaan.");
      return;
    }
    setSubmitting(true);
    try {
      const res = (await apiPost("/api/booking/confirm", {
        building: state.building,
        service: state.service,
        contact: state.contact,
        slot: state.slot,
        holdId: state.holdId,
        acceptedTerms: state.acceptedTerms,
        acceptedPrivacy: state.acceptedPrivacy,
        marketingConsent: state.marketingConsent,
        idempotencyKey: state.idempotencyKey,
        honeypot: (document.getElementById("website") as HTMLInputElement | null)?.value || "",
        formRenderedAt: renderedAt.current,
      })) as ApiResult<any>;

      if (res.ok) {
        // Het booking_confirmed-event wordt bewust NIET hier gevuurd: de regel hieronder
        // is een harde paginanavigatie, die de dataLayer van deze pagina meteen weggooit
        // voordat GTM de push kan verwerken. Het event wordt in plaats daarvan op
        // /boeking-bevestigd zelf gevuurd, zie BookingConfirmed.tsx + initLoadEvents() in
        // client/entries/site.ts.
        setConfirmedBooking(res.data);
        reset();
        window.location.href = `/boeking-bevestigd?nr=${encodeURIComponent(res.data.bookingNumber)}`;
      } else {
        setSubmitError(res.error);
      }
    } catch {
      setSubmitError("Er ging iets mis bij het bevestigen. Probeer het opnieuw.");
    } finally {
      setSubmitting(false);
    }
  }

  if (confirmedBooking) {
    return (
      <SuccessState title="Uw afspraak is bevestigd!">
        <p>Boekingsnummer: {confirmedBooking.bookingNumber}</p>
      </SuccessState>
    );
  }

  return (
    <div>
      <ol className="stepper" aria-label="Voortgang boeking">
        {STEPS.map((label, i) => {
          const stepNum = i + 1;
          const stateAttr = stepNum === state.step ? "active" : stepNum < state.step ? "done" : "upcoming";
          return (
            <li key={label} className="stepper__step" data-state={stateAttr}>
              <span className="stepper__circle" aria-hidden="true">
                {stepNum < state.step ? <CheckIcon size={16} /> : stepNum}
              </span>
              <span className="stepper__label">{label}</span>
              {i < STEPS.length - 1 && <span className="stepper__line" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>

      <div className="booking-layout">
        <div className="booking-panel">
          <div className="card card-pad">
            {state.step === 1 && <BuildingSelector building={state.building} onChange={(building) => update({ building })} />}
            {state.step === 2 && <ServiceSelector service={state.service} onChange={(service) => update({ service })} />}
            {state.step === 3 && (
              <AddressForm contact={state.contact} onChange={(contact) => update({ contact })} errors={validateAddressForm(state.contact)} />
            )}
            {state.step === 4 && (
              <DateTimeStep
                slot={state.slot}
                holdId={state.holdId}
                onSlotChange={(slot) => update({ slot })}
                onHoldChange={(holdId) => update({ holdId })}
                postalCode={state.contact.postalCode || undefined}
              />
            )}
            {state.step === 5 && (
              <BookingReview state={state} breakdown={breakdown} onUpdate={update} submitError={submitError} />
            )}

            {/* Honeypot: onzichtbaar voor mensen, bots vullen dit vaak automatisch in */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="website">Website (niet invullen)</label>
              <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
            </div>

            <div className="flex justify-between mt-6">
              {state.step > 1 ? (
                <button type="button" className="btn btn-ghost" onClick={goBack}>
                  ← Vorige stap
                </button>
              ) : (
                <span />
              )}
              {state.step < 5 ? (
                <button type="button" className="btn btn-primary" onClick={goNext} disabled={!canProceed}>
                  Volgende stap
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={confirmBooking}
                  disabled={submitting || !state.acceptedTerms || !state.acceptedPrivacy || Boolean(breakdown?.attestNotRequired)}
                >
                  {submitting ? "Bezig met bevestigen…" : "Bevestig afspraak"}
                </button>
              )}
            </div>
          </div>
        </div>
        <PriceSummaryPanel breakdown={breakdown} loading={loading} />
      </div>
      <MobileStickyPrice breakdown={breakdown} visible={state.step < 5} />
    </div>
  );
}
